import React, { useEffect, useState } from "react";
import {
  EditorState,
  ContentState,
  convertToRaw,
  convertFromHTML,
} from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { Box, Typography, Paper, Button } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../store/Index";
import draftToHtml from "draftjs-to-html";
import apiService from "../../service/Api";
import { showErrorToast, showSuccessToast } from "./ShowToast";

interface ObituaryEditorProps {
  obituary: string;
  deceasedId?: number;
  onSaveSuccess?: (htmlContent: string) => void;
  onCancel?: () => void;
}

const createEditorStateFromHtml = (html: string) => {
  try {
    if (!html?.trim()) {
      return EditorState.createEmpty();
    }

    const blocksFromHTML = convertFromHTML(html);
    const contentState = ContentState.createFromBlockArray(
      blocksFromHTML.contentBlocks,
      blocksFromHTML.entityMap,
    );

    return EditorState.createWithContent(contentState);
  } catch (error) {
    console.error("Error initializing editor state:", error);
    return EditorState.createEmpty();
  }
};

const ObituaryEditor: React.FC<ObituaryEditorProps> = ({
  obituary,
  deceasedId,
  onSaveSuccess,
  onCancel,
}) => {
  const theme = useSelector((state: RootState) => state.theme);

  const [editorState, setEditorState] = useState<EditorState>(
    createEditorStateFromHtml(obituary),
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setEditorState(createEditorStateFromHtml(obituary));
  }, [obituary]);

  const handleEditorChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState);
  };

  const handleSaveClick = async () => {
    if (!deceasedId) {
      showErrorToast("Missing deceased ID");
      return;
    }

    try {
      setIsSaving(true);

      const rawContent = convertToRaw(editorState.getCurrentContent());
      const htmlContent = draftToHtml(rawContent);

      await apiService.patch(`/api/individuals/deceased/${deceasedId}/`, {
        orbituary: htmlContent,
      });

      showSuccessToast("Obituary saved successfully");

      onSaveSuccess?.(htmlContent);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unexpected error occurred";
      showErrorToast(`Error updating obituary: ${errorMessage}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Paper
      sx={{ p: 3, backgroundColor: theme.primary_colors[800], boxShadow: 0 }}
    >
      <Typography variant="h5" sx={{ mb: 2, color: theme.primary_colors[700] }}>
        Write Obituary
      </Typography>

      <Box
        sx={{
          ".rdw-editor-toolbar": {
            backgroundColor: theme.primary_colors[900],
            borderColor: theme.primary_colors[50],
            color: theme.primary_colors[700],
          },
          ".rdw-option-wrapper, .rdw-dropdown-wrapper": {
            backgroundColor: theme.primary_colors[900],
            color: theme.primary_colors[700],
            borderColor: theme.primary_colors[50],
          },
          ".rdw-option-wrapper:hover, .rdw-option-active": {
            backgroundColor: theme.primary_colors[600],
          },
          ".rdw-editor-main": {
            minHeight: "200px",
            backgroundColor: theme.primary_colors[900],
            color: theme.primary_colors[700],
            border: `1px solid ${theme.primary_colors[300]}`,
            padding: "8px",
          },
          ".DraftEditor-root": {
            color: theme.primary_colors[700],
            "& p": { color: theme.primary_colors[700] },
            "& h1, & h2, & h3, & h4, & h5, & h6": {
              color: theme.primary_colors[700],
            },
            "& a": {
              color: theme.primary_colors[10],
            },
            lineHeight: 1.7,
            fontSize: { xs: "0.875rem", md: "1rem" },
          },
        }}
      >
        <Editor
          editorState={editorState}
          onEditorStateChange={handleEditorChange}
          placeholder="Start writing the obituary here..."
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
        <Button
          variant="contained"
          sx={{
            backgroundColor: theme.primary_colors[500],
            "&:hover": { backgroundColor: theme.primary_colors[400] },
          }}
          onClick={handleSaveClick}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Save Obituary"}
        </Button>

        {onCancel && (
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default ObituaryEditor;
